import React, { useState, useEffect, useMemo } from 'react';
import { MessageSquare, Plus } from 'lucide-react';
import { clientService } from '../../../services/clientService';
import { toast } from 'sonner';
import NoteCard from './NoteCard';
import NoteFilters, { type Filters } from './NoteFilters';
import AddNoteModal from './AddNoteModal';
import NoteProposalsStrip from './NoteProposalsStrip';
import { useTasks } from '../../../hooks/useTasksQuery';
import type { Note, NoteCreateRequest } from '../../../types/notes';

interface NotesTabProps {
  clientId: string;
  /** TASK-930: the fan-out reads the client's name to resolve who "she" is. */
  clientName?: string;
}

const NotesTab: React.FC<NotesTabProps> = ({ clientId, clientName }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [totalNotes, setTotalNotes] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    type: 'all',
    dateRange: 'all',
    search: '',
  });
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [offset, setOffset] = useState(0);
  const LIMIT = 50;

  // TASK-351: Add Note Modal state
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);

  // TASK-930: the note just saved, offered back for structuring. Held here rather than in the
  // modal because the strip outlives the modal — the advisor reviews proposals against the
  // note list, not inside the form they have already submitted.
  const [structuring, setStructuring] = useState<{ content: string; noteId?: string } | null>(null);

  // Notes store `task_ids`, not titles, so a note card can only ever say "3 follow-ups".
  // One client-scoped read resolves the titles for every card on the page.
  const { data: tasksData } = useTasks({ client_id: clientId, limit: 200 });
  const tasksById = useMemo(
    () => new Map((tasksData?.tasks ?? []).map((t) => [t.id, t])),
    [tasksData],
  );

  // Fetch notes on mount and when filters change
  useEffect(() => {
    fetchNotes(true);
  }, [clientId, filters]);

  const fetchNotes = async (reset: boolean = false) => {
    if (reset) {
      setLoading(true);
      setOffset(0);
    } else {
      setLoadingMore(true);
    }

    try {
      // Calculate date range for API params
      const dateParams = calculateDateRange(filters.dateRange);

      const response = await clientService.getNotes(clientId, {
        type: filters.type === 'all' ? undefined : filters.type,
        search: filters.search || undefined,
        limit: LIMIT,
        offset: reset ? 0 : offset,
        ...dateParams,
      });

      if (reset) {
        setNotes(response.notes);
        setOffset(response.notes.length);
      } else {
        setNotes((prev) => [...prev, ...response.notes]);
        setOffset((prev) => prev + response.notes.length);
      }

      setTotalNotes(response.total);
      setHasMore(response.has_more);
    } catch (error: any) {
      console.error('Error fetching notes:', error);

      // If endpoint doesn't exist (404), silently show empty state
      // This is expected until backend implements the notes endpoint
      if (error?.response?.status === 404 || error?.status === 404) {
        setNotes([]);
        setTotalNotes(0);
        setHasMore(false);
      } else {
        // For other errors, show error toast
        toast.error('Failed to load notes');
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Calculate date range for API
  const calculateDateRange = (dateRange: Filters['dateRange']) => {
    if (dateRange === 'all') return {};

    const now = new Date();
    const startDate = new Date();

    switch (dateRange) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setDate(now.getDate() - 30);
        break;
      case 'quarter':
        startDate.setMonth(now.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
    }

    return {
      start_date: startDate.toISOString(),
      end_date: now.toISOString(),
    };
  };

  const handleFilterChange = (newFilters: Filters) => {
    setFilters(newFilters);
    setExpandedNotes(new Set()); // Reset expanded notes when filters change
  };

  const toggleNoteExpansion = (noteId: string) => {
    const newExpanded = new Set(expandedNotes);
    if (newExpanded.has(noteId)) {
      newExpanded.delete(noteId);
    } else {
      newExpanded.add(noteId);
    }
    setExpandedNotes(newExpanded);
  };

  const handleLoadMore = () => {
    fetchNotes(false);
  };

  // TASK-351: Handle note creation
  const handleCreateNote = async (noteData: NoteCreateRequest) => {
    setIsSubmittingNote(true);
    try {
      const created = await clientService.createNote(clientId, noteData);
      toast.success('Note created successfully');
      setIsAddNoteModalOpen(false);
      // TASK-930: a typed note gets the same reading a dictated one gets. Offered, never
      // applied — the strip proposes and the advisor accepts, through the same L1/L3 gate.
      const text = [noteData.summary, noteData.content].filter(Boolean).join('\n\n').trim();
      setStructuring(text ? { content: text, noteId: (created as { id?: string })?.id } : null);
      // Refresh notes list
      fetchNotes(true);
    } catch (error: any) {
      console.error('Error creating note:', error);
      const message = error?.response?.data?.detail || 'Failed to create note';
      toast.error(message);
    } finally {
      setIsSubmittingNote(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-4 animate-pulse">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-200 rounded" />
                <div className="h-3 bg-gray-200 rounded w-20" />
              </div>
              <div className="h-3 bg-gray-200 rounded w-16" />
            </div>
            <div className="h-3 bg-gray-200 rounded w-32 mb-2" />
            <div className="h-4 bg-gray-200 rounded w-full mb-1" />
            <div className="h-4 bg-gray-200 rounded w-3/4" />
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (notes.length === 0) {
    // Check if filters are active
    const hasActiveFilters = filters.type !== 'all' || filters.dateRange !== 'all' || filters.search !== '';

    if (hasActiveFilters) {
      return (
        <div>
          <NoteFilters filters={filters} onFilterChange={handleFilterChange} />
          <div className="text-center py-12 mt-4">
            <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600 font-medium">No notes match your filters</p>
            <p className="text-sm text-gray-500 mt-1">
              Try adjusting your search or filter criteria
            </p>
          </div>
        </div>
      );
    }

    return (
      <>
        <div className="text-center py-12">
          <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No communication notes yet</p>
          <p className="text-sm text-gray-500 mt-1">
            Notes will appear here from meetings, emails, and system events
          </p>
          <button
            onClick={() => setIsAddNoteModalOpen(true)}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#002B45] text-white rounded-lg hover:bg-[#003d5c] transition-colors"
          >
            <Plus size={16} />
            Add Note
          </button>
        </div>

        {/* Add Note Modal */}
        <AddNoteModal
          isOpen={isAddNoteModalOpen}
          onClose={() => setIsAddNoteModalOpen(false)}
          onSubmit={handleCreateNote}
          isLoading={isSubmittingNote}
        />
      </>
    );
  }

  return (
    <div className="space-y-4">
      {/* TASK-930: what the note said, read the way a dictation is read. Above the list, so it
          is the first thing seen after saving and cannot be mistaken for one of the notes. */}
      {structuring && (
        <NoteProposalsStrip
          clientId={clientId}
          clientName={clientName}
          content={structuring.content}
          noteId={structuring.noteId}
          onDismiss={() => setStructuring(null)}
          onCommitted={() => fetchNotes(true)}
        />
      )}

      {/* Header with Filters and Add Button */}
      <div className="flex items-center justify-between gap-4">
        <NoteFilters filters={filters} onFilterChange={handleFilterChange} />
        <button
          onClick={() => setIsAddNoteModalOpen(true)}
          className="flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#002B45] text-white rounded-lg hover:bg-[#003d5c] transition-colors"
        >
          <Plus size={16} />
          Add Note
        </button>
      </div>

      {/* Note count */}
      <div className="text-sm text-gray-600">
        Showing {notes.length} of {totalNotes} notes
      </div>

      {/* Notes List */}
      <div className="space-y-3">
        {notes.map((note) => (
          <NoteCard
            key={note.id}
            note={note}
            isExpanded={expandedNotes.has(note.id)}
            onToggleExpand={() => toggleNoteExpansion(note.id)}
            tasksById={tasksById}
          />
        ))}
      </div>

      {/* Load More Button */}
      {hasMore && (
        <div className="text-center pt-4">
          <button
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="px-6 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loadingMore ? 'Loading...' : 'Load More Notes'}
          </button>
        </div>
      )}

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        onClose={() => setIsAddNoteModalOpen(false)}
        onSubmit={handleCreateNote}
        isLoading={isSubmittingNote}
      />
    </div>
  );
};

export default NotesTab;
